import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { type AdminOrgConnectorSettings, adminClient } from '@/api/adminClient';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminSection } from '@/components/shell/AdminSection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadError } from '@/components/ui/load-state';
import { useOrgScope } from '@/pages/org/OrgLayout';

/** The values in the form, by setting key, for one connector. */
function valuesOf(connector: AdminOrgConnectorSettings): Record<string, string> {
  return Object.fromEntries(
    connector.settings.map((setting) => [setting.key, setting.value ?? '']),
  );
}

export function ConnectorSettingsSection({
  orgId,
  connector,
  onSaved,
}: {
  orgId: string;
  connector: AdminOrgConnectorSettings;
  onSaved: (next: AdminOrgConnectorSettings) => void;
}) {
  const [values, setValues] = useState(() => valuesOf(connector));
  const [saving, setSaving] = useState(false);
  const stored = valuesOf(connector);
  const changed = connector.settings.some(
    (setting) => (values[setting.key] ?? '').trim() !== stored[setting.key],
  );
  const complete = connector.settings.every((setting) => (values[setting.key] ?? '').trim() !== '');
  const hasStored = connector.settings.some((setting) => setting.value !== null);

  const save = async (next: Record<string, string> | null) => {
    setSaving(true);
    try {
      const saved = await adminClient.putOrgConnectorSettings(orgId, connector.integrationId, next);
      onSaved(saved);
      setValues(valuesOf(saved));
      toast.success(next ? 'Settings saved' : 'Settings cleared');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save the settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminSection
      title={connector.title}
      description={
        complete || hasStored
          ? 'Studio writes these values into every call this organization makes, in place of anything its apps send.'
          : 'Calls from this organization are refused until every setting has a value.'
      }
      actions={
        hasStored ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void save(null)}
            disabled={saving}
          >
            Clear
          </Button>
        ) : null
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void save(
            Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value.trim()])),
          );
        }}
      >
        {connector.settings.map((setting) => (
          <div key={setting.key} className="max-w-md space-y-1.5">
            <Label htmlFor={`${connector.integrationId}-${setting.key}`}>
              <span className="font-mono text-xs">{setting.key}</span>
            </Label>
            <Input
              id={`${connector.integrationId}-${setting.key}`}
              value={values[setting.key] ?? ''}
              onChange={(event) =>
                setValues((current) => ({ ...current, [setting.key]: event.target.value }))
              }
              disabled={saving}
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              Used by {setting.operations.join(', ')}.
            </p>
          </div>
        ))}
        {connector.unused.length ? (
          <p className="text-xs text-muted-foreground">
            Also stored, no longer used by this integration: {connector.unused.join(', ')}. Saving
            replaces them.
          </p>
        ) : null}
        <Button type="submit" disabled={saving || !changed || !complete}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          Save
        </Button>
      </form>
    </AdminSection>
  );
}

export function OrgIntegrationSettingsPage() {
  const { orgId } = useOrgScope();
  const [connectors, setConnectors] = useState<AdminOrgConnectorSettings[] | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(
    async () => (await adminClient.listOrgConnectorSettings(orgId)).connectors,
    [orgId],
  );

  useEffect(() => {
    let cancelled = false;
    setConnectors(undefined);
    setError(undefined);
    load()
      .then((next) => {
        if (!cancelled) setConnectors(next);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load the integration settings');
      });
    return () => {
      cancelled = true;
    };
  }, [load, reloadKey]);

  const replace = (next: AdminOrgConnectorSettings) =>
    setConnectors((current) =>
      current?.map((item) => (item.integrationId === next.integrationId ? next : item)),
    );

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Integration settings"
        description="Values RapidSOS sets for this organization on integrations that need them, such as the jurisdiction whose data it may read. The organization cannot see or change them."
      />
      {error ? (
        <LoadError message={error} onRetry={() => setReloadKey((key) => key + 1)} />
      ) : !connectors ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading the integration settings
        </div>
      ) : connectors.length === 0 ? (
        <AdminSection title="Nothing to set">
          <p className="text-sm text-muted-foreground">
            No integration this organization can use takes a setting from RapidSOS.
          </p>
        </AdminSection>
      ) : (
        <div className="space-y-6">
          {connectors.map((connector) => (
            <ConnectorSettingsSection
              key={connector.integrationId}
              orgId={orgId}
              connector={connector}
              onSaved={replace}
            />
          ))}
        </div>
      )}
    </div>
  );
}
